// src/pages/user/UserDashboard.tsx
//
// Route: /user/dashboard — rendered inside <UserLayout> via <Outlet />.
// Reads role from Redux and renders:
//   • ParentDashboard       — role === "parent"   → live data from parentApi
//   • role === "student"  → redirected to their Learn page

import { Link, Navigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUser, selectRole } from "../../app/features/authSlice";
import { useGetLearnersQuery } from "../../app/api/learningApi";
import {
  useGetParentDashboardQuery,
  useInitiatePaymentMutation,
} from "../../app/api/parentApi";
import type {
  ChildCard,
  PaymentSummary,
  ShowcaseSummary,
  MessagePreview,
} from "../../app/api/parentApi";

import { ArrowRight, Award, CalendarDays, ChartColumn, CreditCard, Hand, Lightbulb, NotebookPen, Sparkles, Target, Users } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import Ico from "../../components/Ico";
/* ─────────────────────────────────────────────────────────────────
   TRACK LABEL MAP
───────────────────────────────────────────────────────────────── */

const TRACK_LABELS: Record<string, string> = {
  foundation_builders: "Foundation (6–9)",
  explorers: "Explorers (9–12)",
  innovators: "Innovators (13–18)",
  unassigned: "Unassigned",
};

function trackLabel(track: string) {
  return TRACK_LABELS[track] ?? track;
}

/* ─────────────────────────────────────────────────────────────────
   SHARED UI ATOMS
───────────────────────────────────────────────────────────────── */

function SectionHeading({
  title,
  linkTo,
  linkLabel,
}: {
  title: string;
  linkTo?: string;
  linkLabel?: string;
}) {
  return (
    <div className="mb-4 flex items-center justify-between">
      <h2 className="text-base font-bold tracking-[-0.03em] text-[#16213e]">
        {title}
      </h2>
      {linkTo && (
        <Link
          to={linkTo}
          className="text-xs font-semibold text-[#1b2d5e] transition hover:text-[#4a44b0]"
        >
          {linkLabel ?? "View all"} <ArrowRight className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />
        </Link>
      )}
    </div>
  );
}

function Card({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`rounded-[24px] border border-[#e5e8ef] bg-white shadow-[0_4px_16px_rgba(27,45,94,0.05)] ${className}`}
    >
      {children}
    </div>
  );
}

function ProgressBar({
  pct,
  color = "#1b2d5e",
}: {
  pct: number;
  color?: string;
}) {
  return (
    <div className="h-2 overflow-hidden rounded-full bg-[#ebeff7]">
      <div
        className="h-full rounded-full transition-all duration-700"
        style={{
          width: `${Math.min(100, Math.max(0, pct))}%`,
          background: color,
        }}
      />
    </div>
  );
}

function StatusChip({ status }: { status: string }) {
  const map: Record<string, string> = {
    paid: "bg-[#edfaf4] text-[#2a7a56] border border-[#b6e8d0]",
    pending: "bg-[#fff8ee] text-[#7a4a10] border border-[#f5d9a0]",
    overdue: "bg-[#fff3f3] text-[#9b2a2a] border border-[#f5c0c0]",
    waived: "bg-[#ebeff7] text-[#4a44b0] border border-[#cdd4e2]",
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[12.5px] font-semibold capitalize ${map[status] ?? "bg-[#ebeff7] text-[#4a44b0]"}`}
    >
      {status}
    </span>
  );
}

function InfoPill({
  icon,
  label,
  value,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
}) {
  return (
    <div className="flex flex-col gap-1 rounded-[18px] border border-[#ebeff7] bg-[#f9fafc] px-4 py-3.5">
      <span className="text-base leading-none"><Ico icon={icon} /></span>
      <p className="text-[11.5px] font-semibold uppercase tracking-wider text-[#8189a0]">
        {label}
      </p>
      <p className="text-sm font-bold text-[#16213e]">{value}</p>
    </div>
  );
}

/* Loading skeleton reused across panels */
function Skeleton({ className = "" }: { className?: string }) {
  return (
    <div className={`animate-pulse rounded-xl bg-[#ebeff7] ${className}`} />
  );
}

function CardSkeleton() {
  return (
    <Card className="p-5">
      <div className="flex items-start gap-3">
        <Skeleton className="h-11 w-11 flex-shrink-0 rounded-[14px]" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-3 w-1/2" />
        </div>
      </div>
      <div className="mt-4 space-y-2">
        <Skeleton className="h-2 w-full rounded-full" />
        <div className="grid grid-cols-2 gap-2 pt-2">
          <Skeleton className="h-16 rounded-[18px]" />
          <Skeleton className="h-16 rounded-[18px]" />
        </div>
      </div>
    </Card>
  );
}

/* ─────────────────────────────────────────────────────────────────
   STAT CARD
───────────────────────────────────────────────────────────────── */

function StatCard({
  label,
  value,
  icon,
  bg,
  accent,
}: {
  label: string;
  value: string;
  icon: LucideIcon;
  bg: string;
  accent: string;
}) {
  return (
    <div className="flex items-center gap-3.5 rounded-[22px] border border-[#e5e8ef] bg-white p-4 shadow-[0_4px_16px_rgba(27,45,94,0.04)]">
      <span
        className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-[14px] text-lg"
        style={{ background: bg, color: accent }}
      >
        <Ico icon={icon} />
      </span>
      <div>
        <p className="text-[12.5px] font-semibold uppercase tracking-wider text-[#8189a0]">
          {label}
        </p>
        <p className="mt-0.5 text-xl font-bold tracking-[-0.03em] text-[#16213e]">
          {value}
        </p>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   CHILD CARD  (parent dashboard)
───────────────────────────────────────────────────────────────── */

function ChildCardItem({ child }: { child: ChildCard }) {
  // Online learners: show the learning week (the enrolment's week/session
  // fields belong to the old on-site timetable).
  const { data: learners } = useGetLearnersQuery();
  const learner = learners?.data.find((l) => l.id === child.id && l.active);
  const [initiatePayment, { isLoading: payLoading }] =
    useInitiatePaymentMutation();

  const handlePayNow = async () => {
    if (!child.enrolmentId) return;
    try {
      const result = await initiatePayment({
        enrolmentId: child.enrolmentId,
      }).unwrap();
      // In production: open Paystack inline with result.data.paystackData
      // For now just log the reference — swap with PaystackPop.setup() call
      console.log("Paystack reference:", result.data.reference);
      window.open(
        `https://paystack.com/pay/${result.data.reference}`,
        "_blank",
      );
    } catch (err) {
      console.error("Payment initiation failed:", err);
    }
  };

  return (
    <Card className="p-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-[14px] bg-[#ebeff7] text-sm font-bold text-[#1b2d5e]">
            {child.firstName.charAt(0)}
            {child.lastName.charAt(0)}
          </span>
          <div>
            <p className="font-bold tracking-[-0.02em] text-[#16213e]">
              {child.name}
            </p>
            <p className="text-xs text-[#8189a0]">
              Age {child.age} · {trackLabel(child.track)}
            </p>
          </div>
        </div>
        <StatusChip status={child.paymentStatus} />
      </div>

      {learner?.currentWeek ? (
        <>
          <div className="mt-4">
            <div className="mb-1.5 flex justify-between gap-3 text-xs">
              <span className="truncate font-medium text-[#3b4560]">
                Week {learner.calendarWeek} · {learner.currentWeek.title}
              </span>
              <span className="flex-shrink-0 font-semibold text-[#1b2d5e]">
                {learner.progress?.submitted ?? 0}/{learner.progress?.total ?? 0} done
              </span>
            </div>
            <ProgressBar
              pct={learner.progress?.total ? Math.round((learner.progress.submitted / learner.progress.total) * 100) : 0}
            />
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2">
            <InfoPill
              icon={NotebookPen}
              label="New feedback"
              value={learner.unseenFeedback ? `${learner.unseenFeedback} to read` : "All read"}
            />
            <InfoPill
              icon={Target}
              label="On track"
              value={learner.weeksBehind ? `${learner.weeksBehind} week${learner.weeksBehind > 1 ? "s" : ""} behind` : "Yes"}
            />
          </div>
          <div className="mt-3 flex items-center justify-between gap-3 rounded-[16px] border border-[#ebeff7] bg-[#f9fafc] px-4 py-3">
            <p className="text-xs text-[#5f6780]">
              This week ends{" "}
              {learner.weekEndsAt
                ? new Date(learner.weekEndsAt).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "short" })
                : "soon"}
            </p>
            <Link
              to={`/user/learn/${child.id}`}
              className="flex-shrink-0 rounded-xl bg-[#1b2d5e] px-3 py-1.5 text-[12.5px] font-semibold text-white transition hover:bg-[#24397a]"
            >
              Continue learning{" "}<ArrowRight className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />
            </Link>
          </div>
        </>
      ) : (
        <>
        {/* Progress */}
        {child.enrolmentId ? (
          <div className="mt-4">
            <div className="mb-1.5 flex justify-between text-xs">
              <span className="font-medium text-[#3b4560]">
                {child.quarter ?? "—"} · Week {child.week ?? "—"}
              </span>
              <span className="font-semibold text-[#1b2d5e]">
                {child.progress}%
              </span>
            </div>
            <ProgressBar pct={child.progress} />
          </div>
        ) : (
          <div className="mt-4 rounded-[16px] border border-dashed border-[#cdd4e2] px-4 py-3 text-center">
            <p className="text-xs text-[#8189a0]">
              Not yet enrolled in an active quarter
            </p>
          </div>
        )}

        {/* Meta pills */}
        <div className="mt-4 grid grid-cols-2 gap-2">
          <InfoPill
            icon={CalendarDays}
            label="Next session"
            value={child.nextSession ?? "TBC"}
          />
          <InfoPill
            icon={ChartColumn}
            label="Status"
            value={
              child.studentStatus.charAt(0).toUpperCase() +
              child.studentStatus.slice(1)
            }
          />
        </div>

        {/* Current project + CTA */}
        <div className="mt-3 flex items-center justify-between rounded-[16px] border border-[#ebeff7] bg-[#f9fafc] px-4 py-3">
          <div>
            <p className="text-[11.5px] font-semibold uppercase tracking-wider text-[#8189a0]">
              Current project
            </p>
            <p className="mt-0.5 text-sm font-semibold text-[#16213e]">
              {child.currentProject ?? "Not started"}
            </p>
          </div>
          {child.paymentStatus === "pending" && child.enrolmentId ? (
            <button
              onClick={handlePayNow}
              disabled={payLoading}
              className="rounded-xl bg-[#1b2d5e] px-3 py-1.5 text-[12.5px] font-semibold text-white transition hover:bg-[#24397a] disabled:opacity-50"
            >
              {payLoading ? "…" : "Pay now"}
            </button>
          ) : (
            <Link
              to="/user/reports"
              className="rounded-xl border border-[#dde2ec] px-3 py-1.5 text-[12.5px] font-semibold text-[#1b2d5e] transition hover:bg-[#ebeff7]"
            >
              View report
            </Link>
          )}
        </div>
        </>
      )}
    </Card>
  );
}

/* ─────────────────────────────────────────────────────────────────
   PARENT DASHBOARD — live data
───────────────────────────────────────────────────────────────── */

function ParentDashboard({ firstName }: { firstName: string }) {
  const { data: learners } = useGetLearnersQuery();
  const feedbackToRead = (learners?.data ?? []).reduce((n, l) => n + (l.unseenFeedback ?? 0), 0);
  const { data, isLoading, isError, refetch } = useGetParentDashboardQuery(
    undefined,
    {
      // Re-fetch every 60s so child progress bars update when reports are published
      pollingInterval: 60000,
    },
  );

  const stats = data?.data?.stats;
  const children = data?.data?.children ?? [];
  const payments = data?.data?.payments ?? [];
  const showcases = data?.data?.showcases ?? [];
  const messages = data?.data?.messages ?? [];

  /* ── Error state ── */
  if (isError) {
    return (
      <div className="flex min-h-[40vh] flex-col items-center justify-center gap-4 text-center">
        <p className="text-sm font-semibold text-[#16213e]">
          Couldn't load your dashboard
        </p>
        <p className="max-w-xs text-xs text-[#8189a0]">
          There was a problem reaching the server. Check your connection and try
          again.
        </p>
        <button
          onClick={refetch}
          className="rounded-2xl bg-[#1b2d5e] px-5 py-2.5 text-xs font-semibold text-white"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-[1.6rem] font-bold tracking-[-0.04em] text-[#16213e]">
            Welcome back, {firstName} <Hand className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />
          </h1>
          <p className="mt-1 text-sm text-[#5f6780]">
            Here's everything happening with your children at Future Smart Lab.
          </p>
        </div>
        <Link
          to="/user/enrolments"
          className="mt-3 inline-flex items-center gap-1.5 self-start rounded-2xl bg-[#1b2d5e] px-4 py-2.5 text-xs font-semibold text-white shadow-[0_4px_16px_rgba(27,45,94,0.30)] transition hover:bg-[#24397a] sm:mt-0 sm:self-auto"
        >
          + Enrol a child
        </Link>
      </div>

      {/* ── Stat cards ── */}
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {isLoading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-[72px] rounded-[22px]" />
          ))
        ) : (
          <>
            <StatCard
              label="Children enrolled"
              value={String(stats?.childrenCount ?? 0)}
              icon={Users}
              bg="#ebeff7"
              accent="#1b2d5e"
            />
            <StatCard
              label="Feedback to read"
              value={feedbackToRead ? String(feedbackToRead) : "None"}
              icon={Award}
              bg="#fff4f1"
              accent="#efb0a4"
            />
            <StatCard
              label="Upcoming showcases"
              value={String(stats?.upcomingShowcases ?? 0)}
              icon={Sparkles}
              bg="#edfaf4"
              accent="#6cc2a0"
            />
            <StatCard
              label="Payment due"
              value={
                stats?.pendingAmount
                  ? `₦${Number(stats.pendingAmount).toLocaleString("en-NG")}`
                  : "None"
              }
              icon={CreditCard}
              bg="#fff8ee"
              accent="#f0a050"
            />
          </>
        )}
      </div>

      {/* ── Children cards ── */}
      <div>
        <SectionHeading
          title="My Children"
          linkTo="/user/children"
          linkLabel="Manage"
        />
        {isLoading ? (
          <div className="grid gap-4 md:grid-cols-2">
            <CardSkeleton />
            <CardSkeleton />
          </div>
        ) : children.length === 0 ? (
          <Card className="p-8 text-center">
            <p className="text-sm font-semibold text-[#16213e]">
              No children added yet
            </p>
            <p className="mt-1 text-xs text-[#8189a0]">
              Use the "Enrol a child" button to get started.
            </p>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {children.map((child) => (
              <ChildCardItem key={child.id} child={child} />
            ))}
          </div>
        )}
      </div>

      {/* ── Bottom 3-column grid ── */}
      <div className="grid gap-4 xl:grid-cols-3">
        {/* Upcoming showcases */}
        <Card className="p-5">
          <SectionHeading title="Upcoming Showcases" linkTo="/user/showcases" />
          {isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-20 rounded-[18px]" />
              <Skeleton className="h-20 rounded-[18px]" />
            </div>
          ) : showcases.length === 0 ? (
            <p className="text-center text-xs text-[#8189a0] py-6">
              No upcoming showcases
            </p>
          ) : (
            <div className="space-y-3">
              {showcases.map((s: ShowcaseSummary) => (
                <div
                  key={s.id}
                  className="rounded-[18px] border border-[#ebeff7] bg-[#f9fafc] p-4"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-sm font-semibold text-[#16213e]">
                        {s.name}
                      </p>
                      <p className="mt-0.5 text-xs text-[#8189a0]">
                        {trackLabel(s.track)}
                      </p>
                    </div>
                    <span
                      className={[
                        "flex-shrink-0 rounded-full px-2.5 py-0.5 text-[12.5px] font-semibold",
                        s.daysLeft <= 7
                          ? "bg-[#fff4f1] text-[#7a3020] border border-[#f5c0a0]"
                          : "bg-[#ebeff7] text-[#4a44b0] border border-[#dde2ec]",
                      ].join(" ")}
                    >
                      {s.daysLeft}d
                    </span>
                  </div>
                  <p className="mt-2 text-xs font-medium text-[#5a6278]">
                    <CalendarDays className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}{s.date}
                  </p>
                </div>
              ))}
            </div>
          )}
          <div className="mt-4 rounded-[18px] bg-[#16213e] px-4 py-3.5">
            <p className="text-xs font-medium text-white/60"><Lightbulb className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}Parent tip</p>
            <p className="mt-1.5 text-[13px] leading-5 text-white/80">
              Come to every showcase. Ask your child: "What did you make? Who
              does it help?"
            </p>
          </div>
        </Card>

        {/* Payments */}
        <Card className="p-5">
          <SectionHeading title="Payments" linkTo="/user/payments" />
          {isLoading ? (
            <div className="space-y-2.5">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-[60px] rounded-[16px]" />
              ))}
            </div>
          ) : payments.length === 0 ? (
            <p className="text-center text-xs text-[#8189a0] py-6">
              No payment records yet
            </p>
          ) : (
            <div className="space-y-2.5">
              {payments.map((p: PaymentSummary) => (
                <div
                  key={p.id}
                  className="flex items-center justify-between gap-2 rounded-[16px] border border-[#ebeff7] bg-[#f9fafc] px-4 py-3"
                >
                  <div>
                    <p className="text-[13px] font-semibold text-[#16213e]">
                      {p.desc}
                    </p>
                    <p className="text-[12.5px] text-[#8189a0]">{p.date}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className="text-sm font-bold text-[#16213e]">
                      {p.amount}
                    </span>
                    <StatusChip status={p.status} />
                  </div>
                </div>
              ))}
            </div>
          )}
          <Link
            to="/user/payments"
            className="mt-4 flex w-full items-center justify-center rounded-[18px] border border-[#dde2ec] py-3 text-xs font-semibold text-[#1b2d5e] transition hover:bg-[#ebeff7]"
          >
            View all payments{" "}<ArrowRight className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />
          </Link>
        </Card>

        {/* Messages */}
        <Card className="p-5">
          <SectionHeading title="Messages" linkTo="/user/messages" />
          {isLoading ? (
            <div className="space-y-2.5">
              {Array.from({ length: 2 }).map((_, i) => (
                <Skeleton key={i} className="h-[72px] rounded-[16px]" />
              ))}
            </div>
          ) : messages.length === 0 ? (
            <p className="text-center text-xs text-[#8189a0] py-6">
              No messages yet
            </p>
          ) : (
            <div className="space-y-2.5">
              {messages.map((m: MessagePreview) => (
                <Link
                  key={m.id}
                  to={`/user/messages/${m.threadId}`}
                  className={[
                    "block rounded-[16px] border px-4 py-3.5 transition hover:border-[#c9cfe0]",
                    m.unread
                      ? "border-[#c9cfe0] bg-[#f9fafc]"
                      : "border-[#ebeff7] bg-white",
                  ].join(" ")}
                >
                  <div className="flex items-center gap-2">
                    {m.unread && (
                      <span className="h-2 w-2 flex-shrink-0 rounded-full bg-[#1b2d5e]" />
                    )}
                    <p className="text-[12.5px] font-semibold uppercase tracking-wider text-[#8189a0]">
                      {m.from}
                    </p>
                  </div>
                  <p className="mt-1 text-[13px] font-medium text-[#16213e]">
                    {m.subject}
                  </p>
                  <p className="mt-0.5 text-[12.5px] text-[#8189a0]">{m.time}</p>
                </Link>
              ))}
            </div>
          )}
          <Link
            to="/user/messages"
            className="mt-4 flex w-full items-center justify-center rounded-[18px] border border-[#dde2ec] py-3 text-xs font-semibold text-[#1b2d5e] transition hover:bg-[#ebeff7]"
          >
            View all messages{" "}<ArrowRight className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />
          </Link>
        </Card>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   ROOT EXPORT
───────────────────────────────────────────────────────────────── */

export default function UserDashboard() {
  const user = useSelector(selectUser);
  const role = useSelector(selectRole);
  const firstName = user?.firstName ?? "there";

  if (role === "parent") return <ParentDashboard firstName={firstName} />;

  // Students learn from their Learn page; the old student dashboards were placeholders.
  if (role === "student") return <Navigate to="/user/learn" replace />;

  return (
    <div className="flex min-h-[40vh] flex-col items-center justify-center gap-4 text-center">
      <p className="text-[1.2rem] font-bold text-[#16213e]">
        Welcome, {firstName}
      </p>
      <p className="max-w-md text-sm text-[#5f6780]">
        Your portal is being set up. Contact the academy if something looks
        wrong.
      </p>
      <Link
        to="/user/settings"
        className="rounded-2xl bg-[#1b2d5e] px-5 py-2.5 text-sm font-semibold text-white"
      >
        View settings
      </Link>
    </div>
  );
}
